/**
 * Standing between a character and an NPC (ADR-146 decisions 3 to 5): the range, the check bonus it feeds and the tiers
 * that name it. Pure.
 */

/**
 * The lowest standing with an NPC.
 */
export const NPC_STANDING_MIN = -100;

/**
 * The highest standing with an NPC.
 */
export const NPC_STANDING_MAX = 100;

/**
 * The largest size, up or down, of the points of chance standing adds to one check (the author's answer 3 of 2026-10-29).
 */
export const STANDING_BONUS_CAP = 25;

/**
 * The lowest `per` a check's `standingBonus` may name.
 */
export const MIN_STANDING_PER = 1;

/**
 * The highest `per` a check's `standingBonus` may name.
 */
export const MAX_STANDING_PER = 10;

/**
 * The most tiers one template's `standingTiers` holds.
 */
export const MAX_STANDING_TIERS = 8;

/**
 * The most standing one daily talk may give (`talkStanding` of an NPC template).
 */
export const MAX_TALK_STANDING = 10;

/**
 * One tier of the default table: its id, which a language key names, and the standing it starts at.
 */
export interface DefaultStandingTier {
  readonly id: string;
  readonly from: number;
}

/**
 * The tiers of a template without `standingTiers` (ADR-146 decision 3.3): Hostile from -100, Cold from -49, Neutral from
 * -9, Warm from 10 and Trusted from 50.
 */
export const DEFAULT_STANDING_TIERS: readonly DefaultStandingTier[] = Object.freeze([
  Object.freeze({ id: "hostile", from: -100 }),
  Object.freeze({ id: "cold", from: -49 }),
  Object.freeze({ id: "neutral", from: -9 }),
  Object.freeze({ id: "warm", from: 10 }),
  Object.freeze({ id: "trusted", from: 50 }),
]);

/**
 * Cuts a standing to the NPC range.
 *
 * @param value A whole number.
 * @returns The value within -100 to 100.
 */
export function clampNpcStanding(value: number): number {
  return Math.min(NPC_STANDING_MAX, Math.max(NPC_STANDING_MIN, value));
}

/**
 * The points of chance a check's `standingBonus` adds: `per x standing`, signed, within -25 to +25 (ADR-146 decision 5.2).
 * The caller adds it to the `bonuses` of `checkChance`, which clamps the chance alone.
 *
 * @param standing The character's standing with the conversation's NPC.
 * @param per The check's points per point of standing, 1 to 10.
 * @returns The bonus in points of chance; never negative zero.
 * @example
 * standingBonusPoints(-20, 2);
 * // -25
 */
export function standingBonusPoints(standing: number, per: number): number {
  return Math.min(STANDING_BONUS_CAP, Math.max(-STANDING_BONUS_CAP, per * standing)) + 0;
}

/**
 * The place of the tier a standing falls in: the last tier whose start is at or below the value.
 *
 * @param froms The tiers' starts, ascending, the first at -100.
 * @param standing The standing.
 * @returns The index into the tiers; 0 for a value below the first.
 */
export function standingTierIndex(froms: readonly number[], standing: number): number {
  let found = 0;
  for (let at = 1; at < froms.length; at += 1) {
    if ((froms[at] as number) <= standing) {
      found = at;
    }
  }
  return found;
}

/**
 * Where a standing sits among an NPC's tiers, as the page draws it: the tier's place from 0 and how many tiers there are.
 * Never the standing itself, which stays on the server (ADR-146 decision 8.1).
 */
export interface StandingTierView {
  readonly at: number;
  readonly of: number;
}

/**
 * The tier view of a standing.
 *
 * @param froms The tiers' starts, ascending, the first at -100.
 * @param standing The standing.
 * @returns The tier's place and the tiers' count.
 * @example
 * standingTierView([-100, 10, 25, 50], 15) returns { at: 1, of: 4 }.
 */
export function standingTierView(froms: readonly number[], standing: number): StandingTierView {
  return { at: standingTierIndex(froms, standing), of: froms.length };
}
